import Url from '@core/scripts/helper/url.js';
import fetchWithCSRF from '@core/scripts/helper/fetch-json-with-csrf';
import {useEffect, useRef, useState} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {NAVIGATION_STALE_TIME} from '../constants.js';
import SecondLevelItems from './SecondLevelItems.jsx';
import ThirdLevelColumns from './ThirdLevelColumns.jsx';
import {Icon} from '@bonprix/design-system';
import Device from '@core/scripts/components/device';

export default function NavigationLayer({
    currentPage,
    texts,
    secondLevelNavigationItems,
    changeThirdLevelItemsWithHoverDelay,
    onHandlePointerEnter,
    onHandlePointerLeave,
    onHandleCloseLayer,
}) {
    const initialDelay = useRef(true);
    const hoveredItemChangedTimestamp = useRef(new Date());

    const filteredSubNavigationItems = secondLevelNavigationItems.filter((item) => !item.overviewItem);
    const activeItem = filteredSubNavigationItems.filter((item) => item.active)[0] || filteredSubNavigationItems[0];
    const [hoveredSecondLevelItemId, setHoveredSecondLevelItemId] = useState(activeItem?.id);

    const [thirdLevelNavigationItems, setThirdLevelNavigationItems] = useState(undefined);

    const [delayedSecondLevelNavigationItems, setDelayedSecondLevelNavigationItems] =
        useState(secondLevelNavigationItems);

    const data = useQuery({
        queryKey: ['navigation', 'level', 'third', hoveredSecondLevelItemId, currentPage],
        queryFn: () => fetchThirdLevelNavi(hoveredSecondLevelItemId, currentPage),
        staleTime: NAVIGATION_STALE_TIME,
        enabled: !!hoveredSecondLevelItemId,
        placeholderData: keepPreviousData,
    });

    const fetchThirdLevelNavi = async (shopId, currentPage) => {
        const absoluteUrl = Url.toAbsolute(
            `/ajax/navigation-level/third/json/?shopId=${shopId}&currentPage=${currentPage}`,
        );

        return fetchWithCSRF(absoluteUrl, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
            }),
        })
            .then((response) => {
                if (!response) {
                    throw new Error('Failed to fetch third level navigation items');
                }
                return response;
            })
            .then((data) => {
                return data;
            });
    };

    useEffect(() => {
        setHoveredSecondLevelItemId(activeItem?.id);
        initialDelay.current = true;
    }, [secondLevelNavigationItems]);

    useEffect(() => {
        hoveredItemChangedTimestamp.current = new Date();
        if (hoveredSecondLevelItemId === undefined && activeItem.active) {
            setHoveredSecondLevelItemId(activeItem.id);
        }
    }, [hoveredSecondLevelItemId]);

    useEffect(() => {
        if (!data.data) {
            return;
        }
        changeThirdLevelItemsWithHoverDelay(
            () => {
                setDelayedSecondLevelNavigationItems(secondLevelNavigationItems);
                setThirdLevelNavigationItems(data.data);
            },
            hoveredItemChangedTimestamp.current,
            getInitialDelayAndSetFalse(),
        );
    }, [data.data]);

    const getInitialDelayAndSetFalse = () => {
        if (!initialDelay.current) {
            return false;
        }
        initialDelay.current = false;
        return true;
    };

    // wait until the third level navigation items are fetched
    if (!thirdLevelNavigationItems) {
        return <></>;
    }

    // check if fetch resulted in an empty array
    const thirdLevelEmpty = thirdLevelNavigationItems?.length === 0;

    return (
        <>
            <div className={`navigation__layer`} onPointerEnter={onHandlePointerEnter} onMouseLeave={onHandlePointerLeave}>
                <SecondLevelItems
                    secondLevelNavigationItems={delayedSecondLevelNavigationItems}
                    currentHoveredItemId={hoveredSecondLevelItemId}
                    setCurrentHoveredItemId={setHoveredSecondLevelItemId}
                    thirdLevelEmpty={thirdLevelEmpty}
                />

                <ThirdLevelColumns thirdLevelItems={thirdLevelNavigationItems} texts={texts} />
                {Device.isTouch() && !thirdLevelEmpty && (
                    <button className="navigation__layer-closer" onClick={onHandleCloseLayer}>
                        <Icon variant="closer" />
                    </button>
                )}
            </div>
        </>
    );
}
